import {
  type BackgroundJobName as BackgroundJobNameType,
  type BackgroundJobRunRecord,
  type BackgroundJobRunResultItemRecord,
  type BackgroundJobRunResultRecord,
  type BackgroundJobRunStatus as BackgroundJobRunStatusType,
  type BackgroundJobScheduleRecord,
  type BackgroundJobScheduleStatus as BackgroundJobScheduleStatusType,
  BackgroundJobName as BackgroundJobNameEnum,
  BackgroundJobRunStatus as BackgroundJobRunStatusEnum,
  BackgroundJobRunStatusLabels,
  BackgroundJobScheduleStatus as BackgroundJobScheduleStatusEnum,
  BackgroundJobScheduleStatusLabels
} from '@navio/nsdk';

export type {
  BackgroundJobRunRecord,
  BackgroundJobRunResultItemRecord,
  BackgroundJobRunResultRecord,
  BackgroundJobScheduleRecord
};

export type BackgroundJobRunStatus = BackgroundJobRunStatusType;
export type BackgroundJobScheduleStatus = BackgroundJobScheduleStatusType;
export type BackgroundJobName = BackgroundJobNameType;

export const BackgroundJobRunStatus = BackgroundJobRunStatusEnum;
export const BackgroundJobScheduleStatus = BackgroundJobScheduleStatusEnum;
export const BackgroundJobName = BackgroundJobNameEnum;
export { BackgroundJobRunStatusLabels, BackgroundJobScheduleStatusLabels };

export const BACKGROUND_JOB_TERMINAL_STATUSES: ReadonlySet<BackgroundJobRunStatus> = new Set([
  BackgroundJobRunStatus.SUCCESS,
  BackgroundJobRunStatus.FAILURE,
  BackgroundJobRunStatus.REVOKED,
  BackgroundJobRunStatus.UNKNOWN
]);

export const BACKGROUND_JOB_ACTIVE_STATUSES: ReadonlySet<BackgroundJobRunStatus> = new Set([
  BackgroundJobRunStatus.PENDING,
  BackgroundJobRunStatus.STARTED,
  BackgroundJobRunStatus.RETRY
]);

/** Statuses that may be soft-revoked or hard-terminated from the jobs UI. */
const BACKGROUND_JOB_REVOCABLE_STATUSES: ReadonlySet<BackgroundJobRunStatus> = new Set([
  BackgroundJobRunStatus.PENDING,
  BackgroundJobRunStatus.STARTED,
  BackgroundJobRunStatus.RETRY,
  BackgroundJobRunStatus.STALLED,
  BackgroundJobRunStatus.UNKNOWN
]);

export const isBackgroundJobRunRevocable = (status: BackgroundJobRunStatus | string | null | undefined): boolean =>
  status != null && BACKGROUND_JOB_REVOCABLE_STATUSES.has(status as BackgroundJobRunStatus);

/**
 * Canonical celery task name leaves for adapter work (from {@link BackgroundJobName}).
 * Used to scope fleet "running now" when REST already filters with adapterRelated.
 */
const ADAPTER_RELATED_BACKGROUND_JOB_NAMES: ReadonlySet<string> = new Set(
  Object.entries(BackgroundJobNameEnum)
    .filter(([key]) => key.startsWith('ADAPTER_') || key.startsWith('EONTYRE_')) // nsdk still emits EONTYRE_* job names
    .map(([, value]) => value)
);

/** True when taskName is (or ends with) a known adapter-related {@link BackgroundJobName} value. */
export function isAdapterRelatedBackgroundJobName(taskName: string | null | undefined): boolean {
  if (!taskName) {
    return false;
  }
  if (ADAPTER_RELATED_BACKGROUND_JOB_NAMES.has(taskName)) {
    return true;
  }
  const leaf = taskName.includes('.') ? (taskName.split('.').pop() ?? taskName) : taskName;
  if (ADAPTER_RELATED_BACKGROUND_JOB_NAMES.has(leaf)) {
    return true;
  }
  for (const name of ADAPTER_RELATED_BACKGROUND_JOB_NAMES) {
    if (taskName.includes(name)) {
      return true;
    }
  }
  return false;
}
